import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { GetList, UpdateEntry, DeleteEntry } from "../../providers/dataProvider";
import "./NotificationsPage.scss";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const NotificationsPage = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState("latest");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState("all");
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [search, setSearch] = useState("");
    const [unreadTotal, setUnreadTotal] = useState(null);
    const [readMsg, setReadMsg] = useState(null);
    const [readAllMsg, setReadAllMsg] = useState(null);
    const [actionMsg, setActionMsg] = useState(null);

    const fetchList = () => {
        setLoading(true);
        GetList(
            tab === "latest" ? "notificationpagedlatest" : "notificationpagedarchive",
            { PageNumber: page, PageSize: pageSize },
            (res) => {
                setData(res);
                setLoading(false);
            }
        );
    };

    const fetchUnread = () =>
        GetList("notificationunreadcount", {}, (res) => {
            const d = res?.data;
            const c = [d?.payload?.unreadCount, d?.payload?.count, d?.payload, d?.unreadCount, d?.count, d].find(
                (v) => typeof v === "number"
            );
            if (typeof c === "number") setUnreadTotal(c);
        });

    const notifyParent = () => {
        fetchUnread();
        window.dispatchEvent(new Event("notificationChange"));
    };

    useEffect(() => {
        fetchUnread();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        fetchList();
        // eslint-disable-next-line
    }, [tab, page, pageSize]);

    useEffect(() => {
        if (readMsg?.status === 200 || readMsg?.status === 204) notifyParent();
        // eslint-disable-next-line
    }, [readMsg]);

    useEffect(() => {
        if (readAllMsg?.status === 200 || readAllMsg?.status === 204) {
            fetchList();
            notifyParent();
        }
        // eslint-disable-next-line
    }, [readAllMsg]);

    useEffect(() => {
        if (actionMsg?.status === 200 || actionMsg?.status === 204) {
            fetchList();
            notifyParent();
        } else if (actionMsg?.status === 400) {
            toast.error(actionMsg?.successData?.errors?.[0]?.message || "Action failed. Please try again.");
        }
        // eslint-disable-next-line
    }, [actionMsg]);

    const list = data?.data?.payload || [];
    const meta = data?.data || {};
    const totalCount = meta.totalCount ?? meta.totalRecords ?? meta.totalItems;
    const totalPages = meta.totalPages ?? (totalCount ? Math.ceil(totalCount / pageSize) : null);
    const hasNext = totalPages ? page < totalPages : list.length === pageSize;

    const categories = useMemo(() => [...new Set(list.map((n) => n.category).filter(Boolean))], [list]);
    const visible = list
        .filter((n) => category === "all" || n.category === category)
        .filter((n) => !unreadOnly || !n.isRead)
        .filter((n) =>
            `${n.title ?? ""} ${n.message ?? ""}`
                .toLowerCase()
                .includes(search.trim().toLowerCase())
        );

    const patchLocal = (id, patch) =>
        setData((prev) => ({
            ...prev,
            data: { ...prev?.data, payload: prev?.data?.payload?.map((n) => (n.id === id ? { ...n, ...patch } : n)) },
        }));

    const markAsRead = (id) => {
        UpdateEntry("notificationread", { id }, setReadMsg, "Marked as read successfully", "Failed to mark as read");
        patchLocal(id, { isRead: true });
    };

    const markAllAsRead = () =>
        UpdateEntry("notificationreadall", {}, setReadAllMsg, "All marked as read successfully", "Failed to mark all as read");

    const archive = (id, e) => {
        e.stopPropagation();
        UpdateEntry("notificationarchive", { id }, setActionMsg, "Archived successfully", "Failed to archive");
    };

    const remove = (id, e) => {
        e.stopPropagation();
        DeleteEntry("notificationdelete", { id }, setActionMsg, "Deleted successfully", "Failed to delete");
    };

    const open = (n) => {
        if (!n.isRead) markAsRead(n.id);
        if (n.referenceId) {
            navigate("/job-in-hand", {
                state: { id: n.referenceId, initialTab: "Details", initialProductTab: "ProductList" },
            });
        }
    };

    const isApproval = (n) => n.category?.toLowerCase() === "approval";

    const iconFor = (n) => {
        const t = `${n.title} ${n.message}`.toLowerCase();
        const categoryName = n.category?.toLowerCase();
        if (categoryName === "approval") return { cls: "fa-file-invoice-dollar", tone: "teal" };
        if (categoryName === "order") return { cls: "fa-box", tone: "amber" };
        if (categoryName === "inquiry") return { cls: "fa-bell", tone: "blue" };
        if (t.includes("factory")) return { cls: "fa-industry", tone: "teal" };
        if (t.includes("quotation")) return { cls: "fa-file-invoice-dollar", tone: "blue" };
        if (t.includes("order")) return { cls: "fa-box", tone: "amber" };
        return { cls: "fa-bell", tone: "slate" };
    };

    const rawDate = (n) => n.createdAt || n.createdDate || n.createdOn;
    const formatTime = (n) => {
        const raw = rawDate(n);
        if (!raw) return null;
        return new Date(raw).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    };
    const groupLabel = (n) => {
        const raw = rawDate(n);
        if (!raw) return null;
        const d = new Date(raw);
        const diff = Math.floor((new Date(new Date().toDateString()) - new Date(d.toDateString())) / 86400000);
        if (diff <= 0) return "Today";
        if (diff === 1) return "Yesterday";
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    };

    const grouped = useMemo(() => {
        const out = [];
        visible.forEach((n) => {
            const g = groupLabel(n);
            const last = out[out.length - 1];
            if (last && last.label === g) last.items.push(n);
            else out.push({ label: g, items: [n] });
        });
        return out;
        // eslint-disable-next-line
    }, [visible]);

    const unreadOnPage = list.filter((n) => !n.isRead).length;
    const approvalsOnPage = list.filter(isApproval).length;

    const from = list.length ? (page - 1) * pageSize + 1 : 0;
    const to = (page - 1) * pageSize + list.length;
    const pageNumbers = (() => {
        if (!totalPages) return [page];
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        const arr = [...new Set([1, totalPages, page - 1, page, page + 1])]
            .filter((p) => p >= 1 && p <= totalPages)
            .sort((a, b) => a - b);
        const out = [];
        arr.forEach((p, i) => {
            if (i && p - arr[i - 1] > 1) out.push("...");
            out.push(p);
        });
        return out;
    })();

    useEffect(() => {
        document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
    }, []);

    return (
        <div className="np">
            <div className="card text-end d-none">
                <div className="card-body">
                    <div className="d-flex justify-content-end">
                        <div className="np__search d-none">
                            <i className="fas fa-search"></i>
                            <input
                                placeholder="Search notifications"
                                value={search}
                                onChange={(e) => {
                                    console.log("typing:", e.target.value);
                                    setSearch(e.target.value);
                                }}
                                style={{ position: "relative", zIndex: 10, pointerEvents: "auto" }}
                            />
                        </div>
                        {tab === "latest" && (
                            <button className="np-btn np-btn--solid" onClick={markAllAsRead}>
                                <i class="fas fa-check"></i> Mark all as read
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="np__card">
                <div className="np__card-head">
                    <div className="np__tabs">
                        {["latest", "archived"].map((t) => (
                            <button key={t} className={tab === t ? "active" : ""} onClick={() => { setTab(t); setPage(1); setCategory("all"); }}>
                                <i className={`fas ${t === "latest" ? "fa-bell" : "fa-archive"}`}></i>
                                {t === "latest" ? "Latest" : "Archived"}
                            </button>
                        ))}
                    </div>
                    {tab === "latest" && (
                        <button className="np-btn np-btn--solid" onClick={markAllAsRead}>
                            <i class="fas fa-check"></i> Mark all as read
                        </button>
                    )}
                </div>

                <div className="np__filters">
                    <button className={`chip ${category === "all" ? "active" : ""}`} onClick={() => setCategory("all")}>All</button>
                    {categories.map((c) => (
                        <button key={c} className={`chip ${category === c ? "active" : ""}`} onClick={() => setCategory(c)}>{c}</button>
                    ))}
                    <label className="np__toggle">
                        <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
                        <span className="np__switch"></span>
                        Unread only
                    </label>
                </div>

                <div className="np__list">
                    {loading && [1, 2, 3, 4, 5].map((i) => (
                        <div className="np__skeleton" key={i}>
                            <div className="sk sk--icon"></div>
                            <div className="sk-lines"><div className="sk sk--l1"></div><div className="sk sk--l2"></div></div>
                        </div>
                    ))}

                    {!loading && visible.length === 0 && (
                        <div className="np__empty">
                            <div className="np__empty-icon"><i className="far fa-bell-slash"></i></div>
                            <h4>{tab === "archived" ? "Nothing archived" : "You're all caught up"}</h4>
                            <p>{search || unreadOnly || category !== "all" ? "No notifications match your filters." : "New updates will show up here."}</p>
                        </div>
                    )}

                    {!loading && grouped.map((g, gi) => (
                        <div key={gi}>
                            {g.label && <div className="np__group"><span>{g.label}</span></div>}
                            {g.items.map((n) => {
                                const ic = iconFor(n);
                                return (
                                    <div key={n.id} className={`np__item ${n.isRead ? "" : "unread"}`} onClick={() => open(n)}>
                                        <div className={`np__icon np__icon--${ic.tone}`}><i className={`fas ${ic.cls}`}></i></div>
                                        <div className="np__body">
                                            <div className="np__title">
                                                {!n.isRead && <span className="np__dot"></span>}
                                                {n.title}
                                                {n.category && <span className={`np__tag np__tag--${n.category.toLowerCase()}`}>{n.category}</span>}
                                            </div>
                                            <div className="np__msg">{n.message}</div>
                                        </div>
                                        <div className="np__meta">
                                            {formatTime(n) && <span> {formatTime(n)}</span>}
                                        </div>
                                        <div className="np__actions" onClick={(e) => e.stopPropagation()}>
                                            {!n.isRead && (
                                                <button title="Mark as read" onClick={() => markAsRead(n.id)}><i className="fas fa-check"></i></button>
                                            )}
                                            {tab === "latest" && (isApproval(n)
                                                ? <button title="Archive" onClick={(e) => archive(n.id, e)}><i className="fas fa-archive"></i></button>
                                                : <button className="danger" title="Delete" onClick={(e) => remove(n.id, e)}><i className="far fa-trash-alt"></i></button>)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                <div className="np__footer">
                    <div className="np__show">
                        <span>Show</span>
                        <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                            {PAGE_SIZE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                        <span className="np__entries">
                            {from} - {to}{totalCount != null ? ` of ${totalCount}` : ""} Entries
                        </span>
                    </div>
                    <div className="np__pager">
                        <button disabled={page === 1} onClick={() => setPage(page - 1)} title="Previous"><i className="fas fa-chevron-left"></i></button>
                        {pageNumbers.map((p, i) =>
                            p === "..." ? (
                                <span key={`d${i}`} className="dots">...</span>
                            ) : (
                                <button key={p} className={p === page ? "active" : ""} onClick={() => setPage(p)}>{p}</button>
                            )
                        )}
                        <button disabled={!hasNext} onClick={() => setPage(page + 1)} title="Next"><i className="fas fa-chevron-right"></i></button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationsPage;
